import { merge, Subject } from 'rxjs';
import { filter, take, takeUntil } from 'rxjs/operators';

import { Sound } from './audio/Sound';
import { AudioEditor } from './AudioEditor';

export default class Player {
  public isPlayingSegment: boolean;
  private readonly mainSound: Sound;

  constructor(
    private readonly audioEditor: AudioEditor,
    private readonly playSubject: Subject<void>,
    private readonly pauseSubject: Subject<void>,
    private readonly playerChanged: Subject<{ currentTime: number }>,
    private readonly playerLoaded: Subject<{ duration: number }>,
  ) {
    this.mainSound = new Sound(
      audioEditor.options.audioUrl,
      audioEditor.options.segment.from,
      audioEditor.options.segment.to,
      audioEditor.options.intro.at,
      audioEditor.options.intro.isActive,
      audioEditor.options.outro.at,
      audioEditor.options.outro.isActive,
      audioEditor.options.mix.at,
      audioEditor.options.mix.isActive,
      'current',
    );

    this.audioEditor.unsubscribeAll.subscribe(() => this.mainSound.destroy());
    this.mainSound.onMaxDuration$
      .pipe(
        filter(val => val > 0),
        take(1),
      )
      .subscribe(val => {
        this.playerLoaded.next({ duration: val });
      });
    this.mainSound.onDuration$
      .pipe(takeUntil(this.audioEditor.unsubscribeAll))
      .subscribe(currentTime => {
        this.playerChanged.next({ currentTime });
      });
    this.isPlayingSegment = false;

    merge(
      audioEditor.introPointChanged$,
      audioEditor.outroPointChanged$,
      audioEditor.mixPointChanged$,
    )
      .pipe(takeUntil(this.audioEditor.unsubscribeAll))
      .subscribe(() => {
        this.mainSound.isIntroPointActive = audioEditor.segmentBar.isIntroActive;
        this.mainSound.isMixPointActive = audioEditor.segmentBar.isMixActive;
        this.mainSound.isOutroPointActive = audioEditor.segmentBar.isOutroActive;
        this.mainSound.outroPoint = audioEditor.segmentBar.outroPoint;
        this.mainSound.introPoint = audioEditor.segmentBar.introPoint;
        this.mainSound.mixPoint = audioEditor.segmentBar.mixPoint;
      });

    audioEditor.onStartChanged$.subscribe(start => (this.mainSound.startSegment = start));
    audioEditor.onEndChanged$.subscribe(end => (this.mainSound.endSegment = end));
  }

  public get time() {
    return this.mainSound.getSeek();
  }

  public get isPlaying$() {
    return this.mainSound.playing$;
  }

  public get isPlaying() {
    return this.mainSound.isPlaying;
  }

  public playOrPause() {
    if (this.isPlaying) {
      this.mainSound.pauseMusic();
    } else {
      this.mainSound.playMusic();
    }
  }

  public setPrevSound(sound: Sound) {
    this.mainSound.prevSound = sound;
  }

  public setNextSound(sound: Sound) {
    this.mainSound.nextSound = sound;
  }

  public setTime(time: number) {
    this.mainSound.seekTo(time);
    this.playerChanged.next({ currentTime: this.mainSound.getSeek() });
  }

  public playIntro() {
    this.mainSound.playFromStart();
  }

  public playEnd() {
    this.mainSound.playFromEnd();
  }

  public play() {
    this.playSubject.next();
  }

  public pause() {
    this.pauseSubject.next();
  }
}
